// ─────────────────────────────────────────────────────────────────────────────
//  ServiceSuspended
//
//  The full-screen notice shown in place of the whole application when the
//  facility's TotalCare subscription has lapsed or been suspended. Rendered by
//  LicenseGate; there is no way past it from the browser — no close button, no
//  route behind it. It goes away only when the licence row is renewed and
//  "Check again" (or a reload) finds it in date.
//
//  Two panels: what has happened (left, navy) and how to pay (right) — call
//  first, then Mobile Money or the GCB account. Every number has a copy
//  button, because whoever reads this is usually about to type it into a
//  phone or a bank app.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { Box, Typography, Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import {
  LockOutlined as LockIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  EmailOutlined as EmailIcon,
  Refresh as RefreshIcon,
  VerifiedUserOutlined as SafeIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  PhoneIphone as MomoIcon,
  AccountBalance as BankIcon,
  SupportAgent as AgentIcon,
  CloudDownloadOutlined as DatabaseIcon,
} from '@mui/icons-material';

import { BRAND, FONT_STACK } from '../../brand';
import DatabaseBackupDialog from './DatabaseBackupDialog';

/* Contact details. The server's TOTALCARE_SUPPORT_PHONE / _WHATSAPP / _EMAIL
   win when set; these are what shows otherwise. Leave a field empty and its
   button is simply not shown. */
const FALLBACK_SUPPORT = {
  phone:    '0552260549',
  whatsapp: '',
  email:    '',
};

/* Where renewals are paid. Shown exactly as written here. */
const PAYMENT = {
  momo: {
    network: 'MTN Mobile Money',
    number:  '0552260549',
  },
  bank: {
    name:    'Solute Cyber Technology Ltd',
    account: '7151180003428',
    bank:    'GCB Bank PLC',
    branch:  'Techiman Main',
  },
};

const MOMO_YELLOW = '#FFCB05';

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatMoney = (amount, currency) => {
  if (amount == null) return null;
  return `${currency || 'GHS'} ${Number(amount).toLocaleString('en-GB', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

// 0552260549 → 055 226 0549, the way people read a Ghanaian number aloud.
const spacedPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '');
  return d.length === 10 ? `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}` : p;
};

// Clipboard API needs a secure context; a LAN install on http:// is not one,
// so fall back to the old textarea trick there.
async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch { return false; }
}

const CopyButton = ({ value, label }) => {
  const [done, setDone] = useState(false);
  return (
    <Tooltip title={done ? 'Copied' : `Copy ${label}`} placement="top">
      <IconButton size="small" aria-label={`Copy ${label}`}
        onClick={async () => {
          if (await copyText(value)) { setDone(true); setTimeout(() => setDone(false), 1600); }
        }}
        sx={{
          ml: 0.5, width: 30, height: 30, borderRadius: '8px',
          color: done ? BRAND.greenDark : BRAND.inkMuted,
          background: done ? BRAND.greenSoft : 'transparent',
          '&:hover': { background: done ? BRAND.greenSoft : BRAND.navySoft, color: done ? BRAND.greenDark : BRAND.navy },
        }}>
        {done ? <CheckIcon sx={{ fontSize: 16 }} /> : <CopyIcon sx={{ fontSize: 15 }} />}
      </IconButton>
    </Tooltip>
  );
};

// Left panel: one fact per line on the navy background.
const FactRow = ({ label, value, accent }) => (
  <Box sx={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, py: 1.1,
    '& + &': { borderTop: '1px solid rgba(255,255,255,0.09)' },
  }}>
    <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.62)', flexShrink: 0 }}>{label}</Typography>
    <Typography sx={{
      fontSize: '0.86rem', fontWeight: 700, textAlign: 'right', wordBreak: 'break-word',
      color: accent || '#fff',
    }}>
      {value}
    </Typography>
  </Box>
);

const Step = ({ n, title, body }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
    <Box sx={{
      flexShrink: 0, width: 26, height: 26, borderRadius: '8px',
      background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.16)',
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '0.76rem', fontWeight: 800,
    }}>
      {n}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: '#fff' }}>{title}</Typography>
      <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.66)', lineHeight: 1.55 }}>{body}</Typography>
    </Box>
  </Box>
);

// Right panel: a payment method card.
const PayCard = ({ icon, iconBg, iconColor, title, tag, children }) => (
  <Box sx={{
    border: `1px solid ${BRAND.line}`, borderRadius: '14px', background: BRAND.surface,
    overflow: 'hidden', transition: 'border-color .2s ease, box-shadow .2s ease',
    '&:hover': { borderColor: '#C9D5E5', boxShadow: '0 6px 18px rgba(15, 31, 61, 0.06)' },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, px: 2, py: 1.4, borderBottom: `1px solid ${BRAND.line}`, background: BRAND.canvas }}>
      <Box sx={{
        width: 34, height: 34, borderRadius: '10px', background: iconBg, color: iconColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Typography sx={{ flex: 1, fontSize: '0.92rem', fontWeight: 800, color: BRAND.ink }}>{title}</Typography>
      {tag && (
        <Typography sx={{
          fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.8px', textTransform: 'uppercase',
          color: BRAND.inkMuted, border: `1px solid ${BRAND.line}`, borderRadius: '6px', px: 0.9, py: 0.2,
          background: BRAND.surface,
        }}>
          {tag}
        </Typography>
      )}
    </Box>
    <Box sx={{ px: 2, py: 0.6 }}>{children}</Box>
  </Box>
);

const PayRow = ({ label, value, copy, mono }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1, py: 0.75,
    '& + &': { borderTop: `1px dashed ${BRAND.line}` },
  }}>
    <Typography sx={{ width: 104, flexShrink: 0, fontSize: '0.78rem', color: BRAND.inkMuted }}>{label}</Typography>
    <Typography sx={{
      flex: 1, fontSize: mono ? '0.95rem' : '0.86rem', fontWeight: 700, color: BRAND.ink,
      fontFamily: mono ? '"JetBrains Mono","SFMono-Regular",Consolas,monospace' : FONT_STACK,
      letterSpacing: mono ? '0.4px' : 0, wordBreak: 'break-word',
    }}>
      {value}
    </Typography>
    {copy && <CopyButton value={copy} label={label.toLowerCase()} />}
  </Box>
);

const ServiceSuspended = ({ status, onRetry, checking }) => {
  const [backupOpen, setBackupOpen] = useState(false);
  const suspended = status?.reason === 'suspended';
  const support = {
    phone:    status?.support?.phone    || FALLBACK_SUPPORT.phone,
    whatsapp: status?.support?.whatsapp || FALLBACK_SUPPORT.whatsapp,
    email:    status?.support?.email    || FALLBACK_SUPPORT.email,
  };
  const phoneDigits = (support.phone || '').replace(/[^\d+]/g, '');
  const waDigits = (support.whatsapp || '').replace(/[^\d]/g, '');
  const facility = status?.hospital || 'this facility';
  const amount   = formatMoney(status?.renewalAmount, status?.currency);
  // The domain is often recorded as the facility name itself — no need to say it twice.
  const showDomain = status?.domainName
    && String(status.domainName).trim().toLowerCase() !== String(facility).trim().toLowerCase();
  // What the payer should write as the reference, so the payment can be matched.
  const reference = status?.invoiceNo || facility;

  const title = suspended ? 'Service Suspended' : 'Subscription Expired';
  const lead  = suspended
    ? `Access to TotalCare for ${facility} has been suspended.`
    : `The TotalCare subscription for ${facility} expired on ${formatDate(status?.expiresAt)}.`;

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 20000, overflowY: 'auto',
      fontFamily: FONT_STACK,
      background: `radial-gradient(1200px 600px at 12% -10%, ${BRAND.navyLight}55 0%, transparent 60%),
                   radial-gradient(900px 500px at 100% 110%, ${BRAND.green}2E 0%, transparent 60%),
                   linear-gradient(160deg, #0E2C57 0%, ${BRAND.navyDark} 45%, #07183A 100%)`,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      px: 2, py: { xs: 3, md: 5 },
      '@keyframes tcPulse': {
        '0%':   { boxShadow: `0 0 0 0 ${BRAND.danger}73` },
        '70%':  { boxShadow: `0 0 0 16px ${BRAND.danger}00` },
        '100%': { boxShadow: `0 0 0 0 ${BRAND.danger}00` },
      },
      '@keyframes tcRise': {
        from: { opacity: 0, transform: 'translateY(16px)' },
        to:   { opacity: 1, transform: 'translateY(0)' },
      },
    }}>
      {/* Fine grid over the gradient, fading out towards the edges */}
      <Box aria-hidden sx={{
        position: 'fixed', inset: 0, pointerEvents: 'none',
        backgroundImage: `linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px),
                          linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)`,
        backgroundSize: '44px 44px',
        maskImage: 'radial-gradient(ellipse at center, #000 30%, transparent 75%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, #000 30%, transparent 75%)',
      }} />

      {/* Top bar */}
      <Box sx={{
        position: 'relative', width: '100%', maxWidth: 1040, mb: { xs: 2.5, md: 3.5 }, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box component="img" src="/logo192.png" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }}
            sx={{ width: 34, height: 34, borderRadius: '9px', background: '#fff', p: 0.4 }} />
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.02rem', lineHeight: 1.1 }}>TotalCare</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.7rem', letterSpacing: '0.4px' }}>
              Subscription &amp; Billing
            </Typography>
          </Box>
        </Box>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.8, px: 1.4, py: 0.6, borderRadius: '999px',
          background: `${BRAND.danger}26`, border: `1px solid ${BRAND.danger}66`,
        }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#FF6B6B' }} />
          <Typography sx={{ color: '#FFD3D3', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>
            {suspended ? 'Suspended' : 'Expired'}
          </Typography>
        </Box>
      </Box>

      {/* The card. flexShrink 0: the page is a viewport-high flex column, so
          without it the card is squeezed to the window height and its
          overflow:hidden silently cuts off the bottom — which is exactly how
          the database-download button and the "Check again" footer vanished
          on a laptop screen. Not shrinking lets the page scroll instead. */}
      <Box sx={{
        position: 'relative', width: '100%', maxWidth: 1040, flexShrink: 0,
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: '0.92fr 1.08fr' },
        borderRadius: '20px', overflow: 'hidden',
        boxShadow: '0 40px 100px rgba(2, 8, 24, 0.55), 0 0 0 1px rgba(255,255,255,0.06)',
        animation: 'tcRise .5s ease-out',
      }}>
        {/* ── Left: what has happened ───────────────────────────────── */}
        <Box sx={{
          position: 'relative', p: { xs: 3, sm: 4 }, color: '#fff',
          background: `linear-gradient(165deg, ${BRAND.navy} 0%, #0D2B5C 60%, ${BRAND.navyDark} 100%)`,
          display: 'flex', flexDirection: 'column', gap: 3,
        }}>
          <Box>
            <Box sx={{
              width: 60, height: 60, borderRadius: '16px', mb: 2.2,
              background: `${BRAND.danger}`, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'tcPulse 2.6s infinite',
            }}>
              <LockIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '1.8px', textTransform: 'uppercase', color: '#FF9C9C', mb: 0.8 }}>
              Access restricted
            </Typography>
            <Typography component="h1" sx={{ fontSize: { xs: '1.65rem', sm: '2rem' }, fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.3px' }}>
              {title}
            </Typography>
            <Typography sx={{ mt: 1.4, fontSize: '0.92rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.65 }}>
              {lead} The system is unavailable until the subscription is renewed.
            </Typography>
          </Box>

          <Box sx={{ px: 2, py: 0.6, borderRadius: '14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <FactRow label="Facility" value={facility} />
            {showDomain && <FactRow label="Domain" value={status.domainName} />}
            <FactRow label={suspended ? 'Suspended on' : 'Expired on'} value={formatDate(status?.expiresAt)} />
            {!suspended && status?.daysOverdue > 0 && (
              <FactRow label="Overdue by"
                value={`${status.daysOverdue} day${status.daysOverdue === 1 ? '' : 's'}`} accent="#FF9C9C" />
            )}
            {status?.invoiceNo && <FactRow label="Invoice no." value={status.invoiceNo} />}
            {amount && <FactRow label="Amount due" value={amount} accent="#7FE3A0" />}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '1.4px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
              How to restore access
            </Typography>
            <Step n={1} title="Call us to begin"
                  body={`Ring ${spacedPhone(support.phone) || 'TotalCare'} and we will confirm the renewal amount for your facility.`} />
            <Step n={2} title="Pay by MoMo or bank"
                  body="Use either option on the right — whichever is easier for you." />
            <Step n={3} title="Access returns automatically"
                  body="Once payment is confirmed we renew the subscription. No reinstallation — press “Check again”." />
          </Box>

          {/* The facility's data is theirs whatever happens to the subscription:
              a System Administrator can take a full copy from right here. */}
          <Box sx={{
            mt: 'auto', p: 2, borderRadius: '14px',
            background: 'rgba(46, 158, 79, 0.12)', border: '1px solid rgba(127, 227, 160, 0.28)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#7FE3A0' }}>
              <SafeIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: '0.84rem', fontWeight: 700 }}>
                Your records are safe — nothing has been deleted.
              </Typography>
            </Box>
            <Typography sx={{ mt: 0.6, fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.55 }}>
              Your data belongs to your facility. A System Administrator can download a
              complete copy of the database at any time — renewing or not.
            </Typography>
            <Button onClick={() => setBackupOpen(true)} startIcon={<DatabaseIcon />} variant="outlined" fullWidth
              sx={{
                mt: 1.5, py: 1, textTransform: 'none', fontWeight: 700, borderRadius: '10px',
                color: '#fff', borderColor: 'rgba(255,255,255,0.4)',
                '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.08)' },
              }}>
              Download your database
            </Button>
          </Box>
        </Box>

        {/* ── Right: how to pay ─────────────────────────────────────── */}
        <Box sx={{ background: BRAND.surface, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: { xs: 3, sm: 4 }, pb: { xs: 2.5, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 2.2 }}>
            <Box>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: BRAND.ink }}>Renew your subscription</Typography>
              <Typography sx={{ fontSize: '0.86rem', color: BRAND.inkMuted, mt: 0.4 }}>
                Call first so we can start the process, then pay with any of the options below.
              </Typography>
            </Box>

            {/* Call — the first thing to do */}
            {support.phone && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                p: 2, borderRadius: '14px',
                background: `linear-gradient(135deg, ${BRAND.greenSoft} 0%, #F1FAF4 100%)`,
                border: `1px solid ${BRAND.green}40`,
              }}>
                <Box sx={{
                  width: 46, height: 46, borderRadius: '12px', flexShrink: 0,
                  background: BRAND.green, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <AgentIcon />
                </Box>
                <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: BRAND.greenDark, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Call to get started
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{
                      fontSize: '1.45rem', fontWeight: 800, color: BRAND.ink, letterSpacing: '0.5px',
                      fontFamily: '"JetBrains Mono","SFMono-Regular",Consolas,monospace',
                    }}>
                      {spacedPhone(support.phone)}
                    </Typography>
                    <CopyButton value={phoneDigits} label="phone number" />
                  </Box>
                </Box>
                <Button href={`tel:${phoneDigits}`} startIcon={<PhoneIcon />} variant="contained"
                  sx={{
                    flex: { xs: '1 1 100%', sm: '0 0 auto' }, px: 2.6, py: 1.1,
                    textTransform: 'none', fontWeight: 800, borderRadius: '10px',
                    background: BRAND.green, boxShadow: `0 6px 16px ${BRAND.green}45`,
                    '&:hover': { background: BRAND.greenDark },
                  }}>
                  Call now
                </Button>
              </Box>
            )}

            {(waDigits || support.email) && (
              <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
                {waDigits && (
                  <Button href={`https://wa.me/${waDigits}`} target="_blank" rel="noopener noreferrer"
                    startIcon={<WhatsAppIcon />} variant="outlined"
                    sx={{ flex: '1 1 150px', textTransform: 'none', fontWeight: 700, borderRadius: '10px',
                          color: '#128C4A', borderColor: '#BFE8CF', '&:hover': { borderColor: '#25D366', background: '#EFFAF3' } }}>
                    WhatsApp us
                  </Button>
                )}
                {support.email && (
                  <Button href={`mailto:${support.email}?subject=${encodeURIComponent(`Subscription renewal — ${facility}`)}`}
                    startIcon={<EmailIcon />} variant="outlined"
                    sx={{ flex: '1 1 150px', textTransform: 'none', fontWeight: 700, borderRadius: '10px',
                          color: BRAND.navy, borderColor: BRAND.line, '&:hover': { borderColor: BRAND.navy, background: BRAND.navySoft } }}>
                    {support.email}
                  </Button>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.4 }}>
              <Box sx={{ flex: 1, height: '1px', background: BRAND.line }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '1.2px', textTransform: 'uppercase', color: BRAND.inkFaint }}>
                Payment options
              </Typography>
              <Box sx={{ flex: 1, height: '1px', background: BRAND.line }} />
            </Box>

            <PayCard icon={<MomoIcon sx={{ fontSize: 19 }} />} iconBg={MOMO_YELLOW} iconColor="#1A1A1A"
              title="Mobile Money" tag="Option 1">
              <PayRow label="Network" value={PAYMENT.momo.network} />
              <PayRow label="MoMo number" value={spacedPhone(PAYMENT.momo.number)} copy={PAYMENT.momo.number} mono />
            </PayCard>

            <PayCard icon={<BankIcon sx={{ fontSize: 19 }} />} iconBg={BRAND.navySoft} iconColor={BRAND.navy}
              title="Bank transfer / deposit" tag="Option 2">
              <PayRow label="Account name" value={PAYMENT.bank.name} copy={PAYMENT.bank.name} />
              <PayRow label="Account no." value={PAYMENT.bank.account} copy={PAYMENT.bank.account} mono />
              <PayRow label="Bank" value={PAYMENT.bank.bank} />
              <PayRow label="Branch" value={PAYMENT.bank.branch} />
            </PayCard>

            <Box sx={{
              display: 'flex', gap: 1.2, alignItems: 'flex-start', p: 1.6, borderRadius: '12px',
              background: '#FFF8E6', border: '1px solid #F5DFA6',
            }}>
              <Typography sx={{ fontSize: '0.8rem', color: '#6B4E00', lineHeight: 1.55 }}>
                Use <b>{reference}</b> as the payment reference, then call{' '}
                <b>{spacedPhone(support.phone) || 'us'}</b> with the transaction ID so we can confirm it and restore access.
              </Typography>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{
            mt: 'auto', px: { xs: 3, sm: 4 }, py: 2, background: BRAND.canvas, borderTop: `1px solid ${BRAND.line}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
          }}>
            <Typography sx={{ fontSize: '0.78rem', color: BRAND.inkMuted }}>
              Already paid? Check again once we confirm.
            </Typography>
            <Button onClick={onRetry} disabled={checking} variant="contained"
              startIcon={checking ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.4,
                background: BRAND.navy, boxShadow: 'none',
                '&:hover': { background: BRAND.navyLight, boxShadow: 'none' },
              }}>
              {checking ? 'Checking…' : 'Check again'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Typography sx={{ position: 'relative', mt: 3, fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center' }}>
        TotalCare · Solute Cyber Technology Ltd · Payments go only to the accounts shown above.
      </Typography>

      {/* Above this overlay (z-index 20000), or it would open invisibly behind it. */}
      <DatabaseBackupDialog open={backupOpen} onClose={() => setBackupOpen(false)} zIndex={21000}
        intro={`Take a complete copy of the ${facility} database — every patient, bill, lab and medication record. `
          + 'Only a System Administrator can do this: enter your admin username and password.'} />
    </Box>
  );
};

export default ServiceSuspended;
